import { useState, type FormEvent } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Star, CheckSquare, Square, Users } from 'lucide-react'
import { artistFetch } from '@/lib/artist-api'
import { PageHeader } from '@/components/PageHeader'

// ─── Types ───────────────────────────────────────────────────────────────────

interface CrewMember {
  crewProfileId: string
  name: string
  role: string
  tourName?: string
  showTitle?: string
}

interface TourOption {
  id: string
  name: string
  status: string
}

interface ShowOption {
  id: string
  title: string
  date: string
  venue: string
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export function RateCrewPage() {
  const [contextType, setContextType] = useState<'tour' | 'show'>('tour')
  const [selectedId, setSelectedId] = useState('')

  return (
    <div className="space-y-6">
      <PageHeader
        title="Rate Crew"
        subtitle="Write testimonials for crew who worked on your tours and shows"
      />

      {/* Context selector */}
      <div className="rounded-lg border border-ovation-ink/10 bg-white p-6">
        <h2 className="mb-4 text-xs uppercase tracking-wider text-ovation-muted">
          Select context
        </h2>
        <div className="flex gap-4">
          <div className="flex-1">
            <label className="mb-1 block text-xs text-ovation-muted">Type</label>
            <select
              value={contextType}
              onChange={(e) => {
                setContextType(e.target.value as 'tour' | 'show')
                setSelectedId('')
              }}
              className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
            >
              <option value="tour">Tour</option>
              <option value="show">Show</option>
            </select>
          </div>
          <div className="flex-[2]">
            {contextType === 'tour' ? (
              <TourSelector value={selectedId} onChange={setSelectedId} />
            ) : (
              <ShowSelector value={selectedId} onChange={setSelectedId} />
            )}
          </div>
        </div>
      </div>

      {/* Crew list + rating form */}
      {selectedId && (
        <CrewRatingPanel contextType={contextType} contextId={selectedId} />
      )}
    </div>
  )
}

// ─── Tour Selector ───────────────────────────────────────────────────────────

function TourSelector({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { data } = useQuery<TourOption[]>({
    queryKey: ['tours-for-rating'],
    queryFn: () => artistFetch<TourOption[]>('/tours'),
  })

  return (
    <div>
      <label className="mb-1 block text-xs text-ovation-muted">Tour</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
      >
        <option value="">Select a tour...</option>
        {(data ?? []).map((t) => (
          <option key={t.id} value={t.id}>
            {t.name} ({t.status})
          </option>
        ))}
      </select>
    </div>
  )
}

// ─── Show Selector ───────────────────────────────────────────────────────────

function ShowSelector({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { data } = useQuery<ShowOption[]>({
    queryKey: ['shows-for-rating'],
    queryFn: () => artistFetch<ShowOption[]>('/shows'),
  })

  return (
    <div>
      <label className="mb-1 block text-xs text-ovation-muted">Show</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
      >
        <option value="">Select a show...</option>
        {(data ?? []).map((s) => (
          <option key={s.id} value={s.id}>
            {s.title} - {s.venue} ({s.date.split('T')[0]})
          </option>
        ))}
      </select>
    </div>
  )
}

// ─── Crew Rating Panel ───────────────────────────────────────────────────────

function CrewRatingPanel({
  contextType,
  contextId,
}: {
  contextType: 'tour' | 'show'
  contextId: string
}) {
  const endpoint =
    contextType === 'tour'
      ? `/crew/rate/tour/${contextId}`
      : `/crew/rate/show/${contextId}`

  const { data, isLoading } = useQuery<CrewMember[]>({
    queryKey: ['crew-for-rating', contextType, contextId],
    queryFn: () => artistFetch<CrewMember[]>(endpoint),
  })

  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [rating, setRating] = useState(0)
  const [text, setText] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [success, setSuccess] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const crew = data ?? []

  function toggleAll() {
    if (selected.size === crew.length) {
      setSelected(new Set())
    } else {
      setSelected(new Set(crew.map((c) => c.crewProfileId)))
    }
  }

  function toggle(id: string) {
    const next = new Set(selected)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setSelected(next)
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (selected.size === 0 || rating === 0 || !text.trim()) return
    setError(null)
    setSuccess(null)
    setSubmitting(true)
    try {
      await artistFetch('/crew/rate/bulk', {
        method: 'POST',
        body: {
          crewProfileIds: Array.from(selected),
          text: text.trim(),
          rating,
        },
      })
      setSuccess(`Testimonial submitted for ${selected.size} crew member${selected.size > 1 ? 's' : ''}!`)
      setSelected(new Set())
      setRating(0)
      setText('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit')
    } finally {
      setSubmitting(false)
    }
  }

  if (isLoading) {
    return <p className="text-sm text-ovation-muted">Loading crew...</p>
  }

  if (crew.length === 0) {
    return (
      <div className="rounded-lg border border-ovation-ink/10 bg-white p-6">
        <p className="text-sm text-ovation-muted">
          No crew members found for this {contextType}.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Crew list */}
      <div className="rounded-lg border border-ovation-ink/10 bg-white p-6">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-xs uppercase tracking-wider text-ovation-muted">
            <Users className="h-4 w-4" /> Crew ({crew.length})
          </h2>
          <button
            type="button"
            onClick={toggleAll}
            className="rounded-md border border-ovation-ink/15 px-3 py-1.5 text-xs font-medium hover:bg-ovation-ink/5"
          >
            {selected.size === crew.length ? 'Deselect all' : 'Select all'}
          </button>
        </div>
        <div className="space-y-1">
          {crew.map((c) => (
            <button
              key={c.crewProfileId}
              type="button"
              onClick={() => toggle(c.crewProfileId)}
              className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left hover:bg-ovation-ink/5"
            >
              {selected.has(c.crewProfileId) ? (
                <CheckSquare className="h-4 w-4 text-ovation-ink" />
              ) : (
                <Square className="h-4 w-4 text-ovation-muted" />
              )}
              <span className="text-sm font-medium">{c.name}</span>
              <span className="text-xs text-ovation-muted">{c.role}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Rating form */}
      {selected.size > 0 && (
        <form
          onSubmit={handleSubmit}
          className="rounded-lg border border-ovation-ink/10 bg-white p-6"
        >
          <h2 className="mb-4 text-xs uppercase tracking-wider text-ovation-muted">
            Write testimonial for {selected.size} selected
          </h2>

          {/* Star rating */}
          <div className="mb-4">
            <label className="mb-2 block text-xs text-ovation-muted">Rating</label>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setRating(n)}
                  className="rounded p-1 hover:bg-ovation-ink/5"
                >
                  <Star
                    className={`h-6 w-6 ${
                      n <= rating
                        ? 'fill-amber-400 text-amber-400'
                        : 'text-ovation-ink/20'
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Text */}
          <div className="mb-4">
            <label htmlFor="testimonial-text" className="mb-1 block text-xs text-ovation-muted">
              Testimonial
            </label>
            <textarea
              id="testimonial-text"
              rows={4}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Write about their professionalism, skill, and contribution..."
              required
              className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm focus:border-ovation-ink focus:outline-none"
            />
          </div>

          {error && (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}
          {success && (
            <div className="mb-4 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
              {success}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || rating === 0 || !text.trim()}
            className="rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
          >
            {submitting
              ? 'Submitting...'
              : `Submit for ${selected.size} selected`}
          </button>
        </form>
      )}
    </div>
  )
}
